import React, {
  useRef, useEffect, useState, MutableRefObject, useCallback,
} from 'react';
import videojs from 'video.js';
import 'videojs-mux';
import 'videojs-hls-quality-selector';
import 'videojs-contrib-quality-levels';
import clsx from 'clsx';
import 'video.js/dist/video-js.min.css';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileOnly } from 'react-device-detect';
import { VideoPlayerStyles } from './styles';
import MainPlayAction from './controls/MainPlayActionVideoJS';
import { StaticVideo } from '../../types/items';
import { calculateVideoSize } from './util';
import { VideoMeta } from './types';
import { resetToggleVideoPlayerInMediaViewer } from '../../store/modules/media-viewer/actions';
import { selectMediaViewer } from '../../store/modules/media-viewer/selectors';

type Props = {
  src: string;
  media: 'audio' | 'video';
  nativeControls: boolean;
  poster?: string;
  refPreviewContent: MutableRefObject<HTMLDivElement | null>;
  staticVideo?: StaticVideo;
  videoMeta?: VideoMeta;
  type?: string;
  duration?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onResizePlayer?: (props: { height: string; width: string }) => void;
};

const VideoJS = ({
  src,
  poster,
  refPreviewContent,
  staticVideo,
  videoMeta,
  type,
  onPlay,
  onPause,
  onResizePlayer,
  duration,
}: Props) => {
  const classes = VideoPlayerStyles();
  const refPlayer = useRef<HTMLVideoElement>(null);
  const refPlayerWrapper = useRef<HTMLDivElement>(null);
  const [instancePlayer, setInstancePlayer] = useState<null|videojs.Player>(null);
  const [isReady, setIsReady] = useState(false);
  const dispatch = useDispatch();
  const { shouldRunVideoInMediaViewer } = useSelector(selectMediaViewer);
  const getVideoSize = useCallback(() : { width :string; height: string } | null => {
    if (refPlayerWrapper.current && refPreviewContent.current) {
      if (isMobileOnly && window.matchMedia('(orientation: landscape)').matches) {
        return { width: '100vw', height: '100vh' };
      }
      return calculateVideoSize({
        videoWidth: staticVideo?.width || 1280,
        videoHeight: staticVideo?.height || 720,
        refPreviewContent: refPreviewContent.current,
      });
    }

    return null;
  }, [refPreviewContent, refPlayerWrapper, staticVideo]);

  useEffect(() => {
    const onFullScreenChangeHandler = () => {
      // on enter
      if (document.fullscreenElement?.id) {
      }
      // on exit
      if (!document.fullscreenElement?.id) {
        if (onPause && onPlay) {
          onPause();
          onPlay();
        }
      }
    };

    if (isReady && refPlayer.current) {
      document.addEventListener('fullscreenchange', onFullScreenChangeHandler, false);
    }

    return () => {
      document.removeEventListener('fullscreenchange', onFullScreenChangeHandler, false);
      if (onPause) {
        onPause();
      }
    };
  }, [isReady, refPlayer, onPause, onPlay]);
  useEffect(() => {
    if (refPlayer.current !== null && instancePlayer === null) {
      window.mediaPlayerRef = refPlayer.current;
      const playerJS = videojs(
        refPlayer.current,
        {
          src,
          controls: true,
          poster,
          sources: [{ src, type }],
          controlBar: {
            // @ts-ignore
            pictureInPictureToggle: true,
            volumePanel: true,
            children: [
              'playToggle',
              'remainingTimeDisplay',
              'currentTimeDisplay',
              'timeDivider',
              'durationDisplay',
              'progressControl',
              'liveDisplay',
              'seekToLive',
              'customControlSpacer',
              'playbackRateMenuButton',
              'chaptersButton',
              'descriptionsButton',
              'subsCapsButton',
              'audioTrackButton',
              'volumePanel',
              'fullscreenToggle',
            ],
          },
          plugins: {
            mux: {
              data: {
                env_key: process.env.REACT_APP_MUX_METRICS_TOKEN || '',
                debug: process.env.REACT_APP_BUILD_ENV === 'development',
                // Site Metadata
                viewer_user_id: '', // ex: '12345'
                experiment_name: '', // ex: 'player_test_A'
                sub_property_id: '', // ex: 'cus-1'

                // Player Metadata
                player_name: 'client-app-full', // ex: 'My Main Player'
                player_version: '7.3', // ex: '1.0.0'
                player_init_time: +new Date(), // ex: 1451606400000

                // Video Metadata (cleared with 'videochange' event)
                video_id: '', // ex: 'abcd123'
                video_title: '', // ex: 'My Great Video'
                video_series: '', // ex: 'Weekly Great Videos'
                video_duration: '', // in milliseconds, ex: 120000
                video_stream_type: 'on-demand', // 'live' or 'on-demand'
                video_cdn: 'Fastly', // ex: 'Fastly', 'Akamai'
                ...videoMeta,
              },
            },
          },
        },
        () => {
          if (onResizePlayer) {
            const playerSize = getVideoSize();
            if (playerSize) {
              onResizePlayer(playerSize);
            }
          }
          setInstancePlayer(playerJS);
          setIsReady(true);
        },
      );
      const qualityLevels = playerJS.qualityLevels();

      playerJS.on('loadedmetadata', () => {
        let bestQualityIndex = -1;
        let bestQualityQualityLevel = null;
        for (let index = 0; index < qualityLevels.length; index++) {
          const qualityLevel = qualityLevels[index];
          if ((bestQualityIndex === -1) || (qualityLevel.bitrate > bestQualityQualityLevel.bitrate)) {
            bestQualityQualityLevel = qualityLevel;
            bestQualityIndex = index;
          }
        }

        if (bestQualityIndex !== -1) {
          for (let index = 0; index < qualityLevels.length; index++) {
            qualityLevels[index].enabled = (index === bestQualityIndex);
          }
        }
      });

      qualityLevels.on('addqualitylevel', (event: any) => {
        const { qualityLevel } = event;

        if (qualityLevel.height >= 720) {
          qualityLevel.enabled = true;
        } else {
          qualityLevel.enabled = false;
        }
      });

      playerJS.hlsQualitySelector({
        displayCurrentQuality: true,
      });

      setInstancePlayer(playerJS);
    }
  }, [refPlayer, instancePlayer, src, poster, setIsReady, videoMeta, type, onResizePlayer, getVideoSize]);

  useEffect(() => {
    if (instancePlayer && duration) {
      instancePlayer.duration(duration);
    }
  }, [duration, instancePlayer]);

  useEffect(() => {
    const onResize = async () => {
      if (!isMobileOnly) {
        const videoSize = getVideoSize();

        if (videoSize && refPlayerWrapper.current) {
          if (onResizePlayer) {
            onResizePlayer(videoSize);
          }

          refPlayerWrapper.current.style.height = videoSize.height;
          refPlayerWrapper.current.style.width = videoSize.width;
        }
      }
    };

    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [refPlayerWrapper, staticVideo, onResizePlayer, getVideoSize]);

  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    const setMaxHeightCallback = () => {
      const height = window.innerHeight;
      setMaxHeight(height);
    };
    setMaxHeightCallback();

    window.addEventListener('resize', setMaxHeightCallback);
    window.addEventListener('orientationchange', setMaxHeightCallback);

    return () => {
      window.removeEventListener('resize', setMaxHeightCallback);
      window.removeEventListener('orientationchange', setMaxHeightCallback);
    };
  }, [setMaxHeight]);

  const onTouchPlayer = useCallback(() => {
    if (instancePlayer) {
      const isPlaying = !instancePlayer.paused();
      if (isPlaying) {
        instancePlayer.pause.call(instancePlayer);
      } else {
        instancePlayer.playsinline(true);
        instancePlayer.play.call(instancePlayer);
      }
      instancePlayer.controlBar.show();
    }
  }, [instancePlayer]);

  const togglePlayer = useCallback(() => {
    if (instancePlayer) {
      const isPlaying = !instancePlayer.paused();
      if (isPlaying) {
        instancePlayer.pause.call(instancePlayer);
      } else {
        instancePlayer.playsinline(true);
        instancePlayer.play.call(instancePlayer);
      }
    }
  }, [instancePlayer]);

  useEffect(() => {
    if (shouldRunVideoInMediaViewer) {
      togglePlayer();
      dispatch(resetToggleVideoPlayerInMediaViewer());
    }
  }, [togglePlayer, dispatch, shouldRunVideoInMediaViewer]);
  return (
    <div className={clsx(classes.playerContainer, classes.videoJs)}>
      <div
        data-vjs-player
        style={{
          height: '100%',
          maxHeight: isMobileOnly ? (maxHeight || '100%') : '100%',
          maxWidth: '100%',
          margin: 'auto',
          width: '100vw',
        }}
        ref={refPlayerWrapper}
      >
        <video
          ref={refPlayer}
          className={clsx('video-js', {
            [classes.mobilePlayer]: isMobileOnly,
          })}
          onTouchStart={onTouchPlayer}
          onPlay={onPlay}
          onPause={onPause}
        />
        {instancePlayer && isReady
          && (
            <MainPlayAction
              refPlayer={refPlayer}
              instancePlayer={instancePlayer}
            />
          )}

      </div>
    </div>
  );
};

export default VideoJS;
